/**
 * ellaApi.js — direct client for the ellafroze.com Lumen API.
 *
 * This is the ONLY module that knows the upstream request/response contract
 * (field names like txtUsername, SelFrmState, _s token, form-encoded POSTs).
 * Everything else in the app calls the named functions exported here.
 *
 * Portability (React Native): this module is DOM-free. To reuse it in RN,
 * swap the token store via setTokenStore() (e.g. AsyncStorage-backed) and set
 * ELLA_API_BASE to the absolute URL "https://ellafroze.com/api".
 *
 * Auth model (matches dist/js/helper.js of the legacy Onsen app):
 *  - GET  → token appended as `?_s=<token>` query param
 *  - POST → token appended as `_s` field in an x-www-form-urlencoded body
 *  - The API always answers HTTP 200 with an envelope:
 *      { status: bool, message: string, data: object|array|null, callback: string }
 *    `callback` is a legacy JS-eval hint for the Onsen app — ignore it.
 */
import axios from "axios";

// Same-origin "/api" in production (the React build is served from ellafroze.com,
// where the Lumen API lives under /api). Local dev proxies /api → ellafroze.com
// (see craco.config.js). Override with REACT_APP_ELLA_API when needed.
export const API_BASE = (process.env.REACT_APP_ELLA_API || "/api").replace(/\/+$/, "");
export const UPLOADS_BASE = `${API_BASE}/uploaded`;

// ---------------------------------------------------------------------------
// Token store (swappable for React Native / tests)
// ---------------------------------------------------------------------------
let tokenStore = {
  get: () => {
    try { return window.localStorage.getItem("ella_token") || ""; } catch { return ""; }
  },
};
export function setTokenStore(store) { tokenStore = store; }
const token = () => tokenStore.get() || "";

const http = axios.create({ baseURL: API_BASE, timeout: 30000 });

/**
 * The API keeps a single active session per user: logging in elsewhere silently
 * invalidates this one, and auth-gated endpoints then answer
 * `{status:false, callback:"doHandlerNotAuthorized()"}`. Mirror the legacy
 * app's doHandlerNotAuthorized by notifying the auth layer (only meaningful
 * when we believed we were logged in).
 */
function detectRevokedSession(data) {
  if (!data || data.status !== false) return;
  if (String(data.callback || "").includes("doHandlerNotAuthorized") && token()) {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("ella:unauthorized"));
    }
  }
}

async function apiGet(path, params = {}) {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  );
  const { data } = await http.get(path, { params: { ...clean, _s: token() } });
  detectRevokedSession(data);
  return data;
}

async function apiPost(path, form = {}) {
  const body = new URLSearchParams();
  Object.entries(form).forEach(([k, v]) => {
    if (v !== undefined && v !== null) body.append(k, String(v));
  });
  body.append("_s", token());
  const { data } = await http.post(path, body);
  detectRevokedSession(data);
  return data;
}

// ---------------------------------------------------------------------------
// Money helper — discount semantics used by the API's doPay (authoritative):
//   DiscountType 1 → flat amount off   (Price - Discount)
//   DiscountType 2 → percentage off    (Price - Price*Discount/100)
// ---------------------------------------------------------------------------
export function effectivePrice(price, discountType, discount) {
  const p = Number(price || 0);
  const d = Number(discount || 0);
  const t = Number(discountType || 0);
  if (t === 1) return Math.max(0, p - d);
  if (t === 2) return Math.max(0, p - (p * d) / 100);
  return p;
}
export const hasDiscount = (discountType) => {
  const t = Number(discountType || 0);
  return t === 1 || t === 2;
};
export const discountLabel = (discountType, discount, fmt) => {
  const t = Number(discountType || 0);
  if (t === 2) return `-${Number(discount || 0)}%`;
  if (t === 1) return `Hemat ${fmt(discount)}`;
  return "";
};

export const fmtIDR = (v) => "Rp " + Number(v || 0).toLocaleString("id-ID");

// Image URL helpers (uploaded content stays on the API host)
export const bannerImg = (p) => `${UPLOADS_BASE}/banner/${p}`;
export const categoryImg = (p) => `${UPLOADS_BASE}/category/${p}`;
export const productImg = (p) => `${UPLOADS_BASE}/product/${p}`;
export const paymentImg = (p) => `${UPLOADS_BASE}/${p}`; // e.g. "payments/bca.png"
// Bundled with the build (self-contained). PUBLIC_URL makes it work when the
// app is served from a subpath (e.g. a /v2 staging deploy).
export const LOGO = `${process.env.PUBLIC_URL || ""}/img/logo.png`;

// ---------------------------------------------------------------------------
// Public catalog
// ---------------------------------------------------------------------------
export const getBranches = () => apiGet("external/getBranch");
export const getBanners = () => apiGet("external/getBanner");
export const getCategories = () => apiGet("external/getCategory");

/** BranchID / CatID / Keyword are all optional filters. */
export const getAllProducts = ({ branchId, categoryId, keyword } = {}) =>
  apiGet("external/getAllProduct", {
    BranchID: branchId || "",
    CatID: categoryId || "",
    Keyword: keyword || "",
  });

/** Requires login upstream; returns 10 newest actively-discounted products. */
export const getDiscounts = (branchId) =>
  apiGet("external/getDiscount", { BranchID: branchId || "" });

export const getProductDetail = (productId) =>
  apiGet("external/getProductDetail", { _i: productId });
export const getProductImages = (productId) =>
  apiGet("external/getProductImage", { _i: productId });
export const getProductPrices = (productId) =>
  apiGet("external/getProductPrice", { ProductID: productId });

// ---------------------------------------------------------------------------
// Auth + terms & conditions
// ---------------------------------------------------------------------------

/**
 * Login. On success `data` = { Token, Name, TermsRequired, TermsAccepted, Terms }
 * — Terms is { ID, Code, Version, Title, Content } of the active T&C.
 * NOTE: the API keeps ONE active session per user (token stored in MS_USER.Field2).
 */
export const doLogin = (username, password) =>
  apiPost("external/doLogin", { txtUsername: username, txtPassword: password });

/** Google sign-in — send the Google profile + ID token to the backend
 *  (legacy `external/doAuthGoogle`: ID/Name/Email/TokenID) → session Token. */
export const doAuthGoogle = ({ id, name, email, tokenId }) =>
  apiPost("external/doAuthGoogle", { ID: id, Name: name, Email: email, TokenID: tokenId });

/** privacyConsent "1" records the acceptance in TR_TERMS_ACCEPTANCE (source web-register). */
export const doRegister = ({ name, username, password, privacyConsent = true }) =>
  apiPost("external/doRegister", {
    txtName: name,
    txtUsername: username,
    txtPassword: password,
    privacyConsent: privacyConsent ? "1" : "0",
  });

export const doLogout = () => apiPost("external/doLogout");

/**
 * Careful: this immediately resets the account's password to a random one and
 * emails it (only for accounts with RegisterFrom manual/app and an email set).
 */
export const doResetPassword = (email) =>
  apiPost("external/doReset", { txtEmail: email });

/** Active T&C content (public) → { ID, Code, Version, Title, Content }. */
export const getActiveTerms = () => apiGet("external/getActiveTermsCondition");

/** Auth required → { TermsRequired, TermsAccepted, Terms } for the logged-in customer. */
export const getTermsStatus = () => apiGet("external/getTermsAcceptanceStatus");

/** Records acceptance of the active T&C (source web-home). */
export const doAcceptTerms = () =>
  apiPost("external/doAcceptTermsCondition", { termsConsent: "1" });

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
export const getUser = () => apiGet("external/getUser"); // → { Name, Phone, Email }

/**
 * IMPORTANT: the API overwrites all three columns — always send name, phone AND
 * email, otherwise the missing ones are wiped to NULL (and email is the login key).
 */
export const doUpdateUser = ({ name, phone, email }) =>
  apiPost("external/doUpdateUser", {
    txtFrmName: name,
    txtFrmPhone: phone,
    txtFrmEmail: email,
  });

// ---------------------------------------------------------------------------
// Addresses (cascading geo pickers use MS_REFERENCES ids)
// ---------------------------------------------------------------------------
export const getStates = () => apiGet("global/getState");
export const getCities = (stateId) => apiGet("global/getCity", { stateID: stateId });
export const getDistricts = (cityId) => apiGet("global/getDistrict", { cityID: cityId });

export const getAddresses = () => apiGet("external/getUserAddress");

export const saveAddress = ({
  id = "",
  label,
  phone,
  stateId,
  cityId,
  districtId,
  postalCode = "",
  address,
  isDefault = false,
  action = "add", // "add" | "edit"
}) =>
  apiPost("external/doSaveAddress", {
    hdnFrmID: id,
    hdnAction: action,
    txtAddressName: label,
    txtFrmPhone: phone,
    SelFrmState: stateId,
    SelFrmCity: cityId,
    SelFrmDistrict: districtId,
    txtPostalCode: postalCode,
    txtAddressDetail: address,
    chkDefaultAddress: isDefault ? "1" : "0",
  });

export const removeAddress = (id) =>
  apiPost("external/doRemoveAddress", { hdnFrmID: id });
export const setPrimaryAddress = (id) =>
  apiPost("external/doSetPrimaryAddress", { hdnFrmID: id });

// ---------------------------------------------------------------------------
// Cart
// ---------------------------------------------------------------------------
export const getCart = () => apiGet("external/getCart");

/**
 * Upstream semantics of doSaveCart:
 *  - item NOT in cart yet → inserts with the given qty (Source irrelevant)
 *  - item in cart + Source "product" → increments stored qty by 1 (legacy "Beli")
 *  - item in cart + Source other ("cart") → SETS qty to the given value
 *  - qty 0 → removes the row
 * Stock-checked server-side ("Stock hanya tersedia N" on overflow).
 */
export const saveCartItem = ({ productId, qty, notes = "", source = "cart" }) =>
  apiPost("external/doSaveCart", {
    ProductID: productId,
    Qty: String(qty),
    Notes: notes,
    Source: source,
  });

// ---------------------------------------------------------------------------
// Checkout
// ---------------------------------------------------------------------------

/**
 * Delivery fee for the current cart to the customer's DEFAULT address.
 * → data = [ { Branch, Fee, IsFound } ]  (one row per branch in the cart)
 * IsFound 0 means no MS_DELIVERYCOST route exists → checkout must be blocked.
 */
export const calculateDelivery = () => apiGet("external/doCalculateDelivery");

/** Same but for an explicit address id (used when previewing another address). */
export const calculateDeliveryFor = (addressId) =>
  apiGet("external/doCalculateDeliveryNew", { addressId });

/** → rows { Category, ID: "code|Label", ImagePath } e.g. "bca|BCA Virtual Account". */
export const getPaymentMethods = () => apiGet("external/getPaymentMethod");

/**
 * Create the order + Midtrans charge. `methodCode` is the BARE code — the part
 * before "|" in the payment method ID (e.g. "bca", "alfamart", "gopay") — it is
 * passed straight to Midtrans as bank/store name.
 * Success data = { PaymentMethodCategory, PaymentMethodLogo, PaymentMethod,
 *                  ExpiredDate, ReferenceID, GrossAmount, GoPayDeepLink }
 * (ReferenceID = VA number / payment code / bill key; empty for gopay & direct.)
 * Uses the DEFAULT address and empties the server cart on success.
 */
export const doPay = (methodCode) =>
  apiPost("external/doPay", { paymentMethod: methodCode });

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

/** Status: 2 Diproses, 3 Dikirim, 4 Selesai, 5 Batal (1/unpaid has its own endpoint). */
export const getTransactions = (status) =>
  apiGet("external/getTransaction", { Status: String(status) });

/**
 * Unpaid orders (also auto-cancels expired ones server-side and restores stock).
 * → rows { OrderID, ID, PaymentMethodCategory, PaymentMethod("code|Label"),
 *          PaymentLogo, ReferenceID, GopayDeepLink, GrossAmount, IsPaid,
 *          ExpiredDate, IsExpired }
 */
export const getUnpaidTransactions = () => apiGet("external/getUnpaidTransaction");

/** id = the payment/invoice id (INV/yyyymmdd/XXXX). → { ID, paymentData, orderData } */
export const getTransactionDetail = (id) =>
  apiGet("external/getTransactionDetail", { ID: id });

/** Printable invoice URL (opens in new tab). Uses the url-encoded ID from detail. */
export const invoiceUrl = (encodedId) => `${API_BASE}/invoice?i=${encodedId}&_s=${encodeURIComponent(token())}`;

// ---------------------------------------------------------------------------
// Chat, help, notifications
// ---------------------------------------------------------------------------
export const getChatList = () => apiGet("external/getChatList");
export const getChatDetail = (branchId) =>
  apiGet("external/getChatDetail", { BranchID: branchId });
export const sendChatMessage = (branchId, message) =>
  apiPost("external/doSaveMessage", { BranchID: branchId, Message: message });

export const getHelpList = () => apiGet("external/getHelpList");

/** → { cartData, messageData, orderData } counts (zeros when logged out). */
export const getNotifications = () => apiGet("external/getNotification");
